import {
  ActivityType,
  CheckInEventType,
  CheckInSourceType,
  ConnectionDirection,
  ConnectionStatus,
  ControlLinkedLocationDto,
  DeskAmenity,
  ExtraName,
  NotificationCustomActionType,
  NotificationPreferenceCategory,
  NotificationPreferenceType,
  NotificationTypeDto,
  ProfileType,
  RoomState,
  SensorAvailabilityStatus,
} from '@lib/infrastructure';
import {DayName as DayNameUtil} from '@lib/utils';
import {FacilitySubType} from '../../../../../submodules/map/mapiq-map/markers/NodeMarkerData';
import {type LocationFeatureCollection} from '../../../../../submodules/map/types/MpqGeoJSON';
import {Resource} from '../buildingResources';
// TODO: The above should probably not be here.

export type DayName = DayNameUtil;

export type AccessProfile = {
  id: string;
  name: string;
  daysPerWeek: number;
  daysAhead: number;
  registrationDays: DayName[];
  userId: string;
};

export type LocationProfile = {
  id: string;
  name: string;
  allowedLocationIds: string[];
  disallowedLocationIds: string[];
  userId: string;
  allowAll: boolean;
};

export type ParkingQuotaProfile = {
  id: string;
  name: string;
  daysPerWeek: number;
  userId: string;
};

export type AppUser = {
  id: string;
  initials: string;
  directoryObjectId: string;
  subscriptionId: string;
  firstName: string;
  lastName: string;
  email: string;
  defaultBuildingId: string;
  isNew: boolean;
  accessProfileType: ProfileType;
  locationProfileType: ProfileType;
  effectiveAccessProfile: string;
  effectiveLocationProfile: string;
  imageHash?: string;
  parkingQuotaProfile: string;
  parkingLocationProfile: string;
};

export type ParkingPreferences = {
  userDismissedParkingSuggestionPopUp: boolean;
  userDismissedParkingIntroduction: boolean;
  licensePlate: string | null;
  licensePlates: string[] | null;
};

export type OpeningDayException = {
  id: string;
  reason: string;
  startDate: string;
  endDate: string;
  closedNodeIds: string[];
};

export type Building = {
  id: string;
  parentId?: string;
  name: string;
  timeZoneId: string;
  capacity: number;
  floors: string[];
  openingDays: DayName[];
  allowWorkdayWithoutWorkspace: boolean;
};

export type Floor = {
  id: string;
  parentId?: string;
  buildingId: string;
  name: string;
  number: number;
  capacity: number;
  areas: string[];
  hasMap: boolean;
};

export type Area = {
  id: string;
  parentId?: string;
  floorId: string;
  buildingId: string;
  name: string;
  capacity: number;
  hasBookableDesks: boolean;
  deskAmenities: DeskAmenity[];
  activityTypes: ActivityType[];
  type: 'area';
  workspaceBookingPolicyType: 'AreaBooking' | 'DeskBooking' | 'NonBookable';
  adHocCapacity: number;
};

export type Desk = {
  id: string;
  areaId: string;
  name: string;
  isEnabled: boolean;
};

export type FloorMap = {
  id: string;
} & ({type: '2d'; map: string} | {type: '3d'; map: LocationFeatureCollection});

export type Facility = {
  id: string;
  displayName: string;
  facilityType: FacilityType;
};

export type FacilityType = {
  id: FacilitySubType;
  displayName: string;
  lightIconUrl: string;
  boldIconUrl: string;
};

export type DeskSuggestion = {
  id: string;
  desks: string[];
};

export type SuggestedDesk = {
  id: string;
  deskSuggestionId: string;
  score: number;
  explanation: string;
  booked: boolean;
};

export type DeskBookingStatus = {[key: string]: boolean};

export type WorkspaceReservation = {
  id: string;
  startDate: string;
  endDate: string;
  currentMember?: ReservationMember;
  owner?: ReservationMember;
  /**
   * @deprecated
   */
  nodeId: string;
  /**
   * @deprecated
   */
  userId: string;
  /**
   * @deprecated
   */
  buildingId: string;
  /**
   * @deprecated
   */
  buildingName: string;
  /**
   * @deprecated
   */
  floorId: string;
  /**
   * @deprecated
   */
  floorName: string;
  /**
   * @deprecated
   */
  floorHasMap: boolean;
  /**
   * @deprecated
   */
  floorNumber: number;
  /**
   * @deprecated
   */
  areaId: string;
  /**
   * @deprecated
   */
  areaName: string;
  /**
   * @deprecated
   */
  deskId: string;
  /**
   * @deprecated
   */
  deskName: string;
  createdBy?: string;
  createdOrLastModifiedDate?: string;
  groupReservationId?: string;
  status: WorkspaceReservationStatus;
  members: ReservationMember[];
};

export type GroupReservationInviteeSearchResult = {
  id: string;
  name?: string;
  initials: string;
  isNew?: boolean;
  isActive?: boolean;
  email: string;
  directoryObjectId?: string;
  imageHash?: string | null;
  status?: WorkspaceReservationStatus;
};
export type WorkspaceReservationCountPerNode = Record<string, number>;

export type WorkspaceReservationCountPerNodeByDate = Record<string, WorkspaceReservationCountPerNode>;

export type ConnectionUser = {
  id: string;
  connectionId?: string;
  name: string;
  initials: string;
  email: string;
  isActive: boolean;
  isConnecting?: boolean;
  directoryObjectId: string;
  imageHash?: string;
};

export type Connection = {
  id: string;
  connectedUser: string;
  direction: ConnectionDirection;
  status: ConnectionStatus;
  creationTime: string;
  isAccepting: boolean;
  isIgnoring: boolean;
  isDeleting: boolean;
};

export type Installation = {
  deviceId: string;
  platform: string;
  token: string;
  tags: string[];
  version: number;
};

export type NotificationCategory = NotificationPreferenceCategory;

export type NotificationCategoryType = NotificationPreferenceType;

export type NotificationPreference = {
  category: NotificationCategory;
  type: NotificationCategoryType;
  enabled: boolean;
};

export type NotificationType = NotificationTypeDto;

export type NotificationCategoryWithTypes = {
  category: NotificationCategory;
  types: NotificationCategoryType[];
};

export type NotificationMessageValue = {
  BuildingName?: string;
  FloorName?: string;
  InviteeCountText?: string;
  OrganizerName?: string;
  ShiftDate?: string;
  InviteeCount?: number;
  AdminName?: string;
  MinutesUntilRoomIsReleased?: string;
  MeetingRoomName?: string;
  MeetingStartAndEnd?: string;
  NoShowId?: string;
  Link?: string;
  StartDateTimeUtc?: string;
  EndDateTimeUtc?: string;
};
export type Notification = {
  id: string;
  type: NotificationType;
  isRead: boolean;
  messageTemplate: string;
  messageTitle: string;
  message: string;
  messageValues: NotificationMessageValue;
  creationDateTime: string;
  isDeleting: boolean;
  customAction?: NotificationCustomActionType;
  customActionDeadline?: string;
  isCustomActionPerformed?: boolean;
};

export type MeetingsSettings = {
  enabledCalendarIntegration: boolean;
  enabledOffice365: boolean;
  enabledGoogleWorkspace: boolean;
  pageTimespanInDays: number;
  onlineMeetingLinkSupported: boolean;
};

export type WorkingHoursDayInfo = {
  dayName: DayName;
  times: {
    startTimeLocal: string;
    endTimeLocal: string;
  }[];
};
export type WorkingHours = {
  timeZoneId: string;
  days: WorkingHoursDayInfo[];
};

export type WorkspaceLocationPreference = {
  floorId?: string;
  areaId?: string;
  isDeskRequired?: boolean;
  deskAmenities?: string[];
};

export type ParkingLocationPreference = {
  parkingLotId: string;
  parkingZoneId?: string;
};

export type MeetingsUserPreferences = {
  enableCalendar: boolean;
  userClosedConsentTile: boolean;
  userClosedConsentErrorTile: boolean;
  workingHours: WorkingHours | null;
  calendarDomain?: string | null;
};

export interface UserPreferences extends MeetingsUserPreferences, ParkingPreferences {
  workspaceLocation: WorkspaceLocationPreference | null;
  parkingLocation: ParkingLocationPreference | null;
}

export type MeetingResponseStatus = 'Accepted' | 'Declined' | 'Tentative' | 'Pending' | 'Organizer';

export type AttendeeLocation = {
  locationType: MeetingAttendeeType;
  shiftBuildingId?: string;
  shiftBuildingName: string;
};
export type MeetingAttendee = {
  meetingId: string;
  email: string;
  directoryObjectId?: string;
  displayName: string;
  initials: string;
  responseStatus: MeetingResponseStatus;
  locationInfoPerDate: Record<string, AttendeeLocation | undefined>;
  imageHash?: string;
};

export enum RoomActivity {
  InformalMeeting = 'InformalMeeting',
  FormalMeeting = 'FormalMeeting',
  Brainstorm = 'Brainstorm',
  Videoconference = 'Videoconference',
  PrivateCall = 'PrivateCall',
  Collaboration = 'Collaboration',
  Presentation = 'Presentation',
  Conversation = 'Conversation',
  OneToOne = 'OneToOne',
  Lecture = 'Lecture',
}

export enum RoomEquipment {
  PresentationScreen = 'PresentationScreen',
  Webcam = 'Webcam',
  Speakerphone = 'Speakerphone',
  Audio = 'Audio',
  Flipboard = 'Flipboard',
  InteractiveWhiteboard = 'InteractiveWhiteboard',
  Whiteboard = 'Whiteboard',
  Smartboard = 'Smartboard',
  Projector = 'Projector',
}

export type MeetingRoom = {
  id: string;
  displayName: string;
  email: string;
  capacity: number;
  activities: RoomActivity[];
  equipment: RoomEquipment[];
  buildingId: string;
  buildingName: string;
  floorId: string;
  floorName: string;
  roomPictureHash?: string;
  /**
   * The meeting room ID.
   */
  suggestionData?: string;
  type: 'meetingRoom';
};

export type MeetingRoomSuggestion = {
  id: string;
  state: RoomState;
  suggestionId?: string;
  score: number;
  scheduleInformation: ScheduleInformationItem[];
  scheduleAvailability: AttendeeAvailabilityStatus;
  sensorAvailability: SensorAvailabilityStatus;
  /**
   * Number of people detected by sensors.
   */
  numberOfPeople?: number;
  sensorStateTimestamp?: string;
  markedAsBookedLocally?: boolean;
};

export type ScheduleInformationItem = {
  start: string;
  end: string;
  availability: AttendeeAvailabilityStatus;
};

export type Meeting = {
  id: string;
  title: string;
  startDateTime: string;
  endDateTime: string;
  isAllDay: boolean;
  isCanceled: boolean;
  onlineMeetingUrl?: string;
  meetingRooms: string[];
  attendees: MeetingAttendee[];
  message?: string;
  messagePreview?: string;
  organizerTimeZone?: string;
};

export type CheckInStatus = {
  id?: string;
  userId?: string;
  directoryObjectId?: string;
  eventType: CheckInEventType;
  buildingId?: string;
  sourceType?: CheckInSourceType;
};

export type MeetingInviteeSearchResult = {
  /**
   * This is the connection id
   */
  id: string;
  name: string;
  initials: string;
  email: string;
  imageHash: string | null;
  directoryObjectId?: string;
};

export enum AttendeeAvailabilityStatus {
  Available = 'Available',
  Tentative = 'Tentative',
  Unavailable = 'Unavailable',
  Unknown = 'Unknown',
}

export enum MeetingAttendeeType {
  Local = 'Local',
  Remote = 'Remote',
  Unknown = 'Unknown',
}

export type AttendeeAvailability = {
  displayName: string;
  email: string;
  availability: AttendeeAvailabilityStatus;
  initials: string;
  directoryObjectId: string;
  shiftBuildingId: string;
  shiftBuildingName: string;
  locationType: MeetingAttendeeType;
  imageHash: string;
};

export type MeetingSuggestion = {
  id: string;
  timeSlotStart: string;
  timeSlotEnd: string;
  attendeeAvailabilities: AttendeeAvailability[];
};

export type ParkingZone = {
  id: string;
  name: string;
  capacity: number;
  parentId: string;
};

export type ParkingLot = {
  id: string;
  name: string;
  capacity: number;
  parkingZones: string[];
  openingDays: DayName[];
};

export type ParkingReservation = {
  id: string;
  date: string;
  nodeId: string;
  parkingLotId: string;
  zoneId: string;
  parkingLotName: string;
  zoneName: string;
};

export type TimeZone = {
  id: string;
  idWithUtcOffset: string;
};

export type ConnectionSuggestionType = 'Random' | 'CommonFriends';

export type ConnectionSuggestion = {
  id: string;
  userId: string;
  created: string;
  serveId: string;
  suggestions: SuggestedConnection[];
};
export type SuggestedConnection = {
  connectedUser: ConnectionUser;
  score: number;
  type: ConnectionSuggestionType;
  numberOfCommonFriends: number;
};

export type WorkspaceReservationStatus =
  | 'InvitationDeclined'
  | 'InvitationExpired'
  | 'InvitationOverwritten'
  | 'InvitationCancelled'
  | 'Confirmed'
  | 'InvitationPending'
  | 'None';

export type WorkspaceLocation = {
  buildingId: string;
  buildingName: string;
  floorId?: string;
  floorName?: string;
  areaId?: string;
  areaName?: string;
  deskId?: string;
  deskName?: string;
};

export type ReservationMember = {
  userId: string;
  reservationId: string;
  user: {
    id: string;
    directoryObjectId?: string;
    email: string;
    imageHash?: string;
    initials: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
  };
  nodeId: string;
  location: WorkspaceLocation;
  status: WorkspaceReservationStatus;
  remainingDaysToReply?: number;
  expiryDateTime?: string;
  isOwner: boolean;
};

export type GroupWorkspaceReservation = {
  id: string;
  startDate: string;
  endDate: string;
  owner: ReservationMember;
  invitees: ReservationMember[];
  isUpdating: boolean;
};

export type WorkdayStatus = 'OfficeDay' | 'NotWorking' | 'WorkingRemotely';

export type Workday = {
  nodeId: string;
  nodeName: string;
  endDate: string;
  id: string;
  startDate: string;
  status: WorkdayStatus;
  userId: string;
};

export type ExtraType = 'Workday' | 'HybridMeetings' | 'UserProfile';

export type Extra = {
  id: string;
  url: string;
  description: string;
  name: ExtraName;
  type: ExtraType;
};

export type CustomerBranding = {
  title: string;
  bannerUrl?: string;
  logoUrl?: string;
};

export type UserSettings = {
  accessControl?: AccessControlSettings;
};
export type AccessControlSettings = {
  swiftConnect?: SwiftConnectSettings;
};
export type SwiftConnectSettings =
  | {
      clientId: string;
      authority: string;
      isEnabledForUser: true;
      baseUrl: string;
      origoUuid: string;
      applicationId: string;
      organizationName: string;
    }
  | {
      clientId: null;
      authority: null;
      isEnabledForUser: false;
      baseUrl: null;
      origoUuid: null;
      applicationId: null;
      organizationName: null;
    };

export type MeetingRoomAmenities = {
  activities: RoomActivity[];
  equipment: RoomEquipment[];
};

export type TemperatureValues = -2 | -1 | 0 | 1 | 2;
export type BlindsValues = 0 | 25 | 50 | 75 | 100;
export type Controllable = {
  id: string;
  displayName: string;
  locations: ControlLinkedLocationDto[];
} & (
  | {
      type: 'blinds';
      currentValue?: BlindsValues;
    }
  | {
      type: 'temperature';
      currentValue?: TemperatureValues;
    }
);

// Typings for sets

export type Buildings = Record<string, Building>;

export type Floors = Record<string, Floor>;

export type Areas = Record<string, Area>;

export type Desks = Record<string, Desk>;

export type WorkspaceReservations = Record<string, WorkspaceReservation>;

export type Workdays = Record<string, Workday>;

export type GroupWorkspaceReservations = Record<string, GroupWorkspaceReservation>;

export type Connections = Record<string, Connection>;

export type ConnectionUsers = Record<string, ConnectionUser>;

export type DeskSuggestions = Record<string, DeskSuggestion>;

export type SuggestedDesks = Record<string, SuggestedDesk>;

export type AppUsers = Record<string, AppUser>;

export type AccessProfiles = Record<string, AccessProfile>;

export type LocationProfiles = Record<string, LocationProfile>;

export type Notifications = Record<string, Notification>;

export type Meetings = Record<string, Meeting>;

export type MeetingRooms = Record<string, MeetingRoom>;

export type MeetingRoomsSuggestionData = Record<string, MeetingRoomSuggestion>;

export type CheckInStatuses = Record<string, CheckInStatus>;

export type MeetingSuggestions = Record<string, MeetingSuggestion>;

export type ParkingLots = Record<string, ParkingLot>;

export type ParkingZones = Record<string, ParkingZone>;

export type ParkingReservations = Record<string, ParkingReservation>;

export type ParkingReservationCountPerNode = Record<string, number>;

export type ParkingReservationCountPerNodeByDate = Record<string, ParkingReservationCountPerNode>;

export type ParkingLocationProfiles = Record<string, LocationProfile>;

export type ParkingQuotaProfiles = Record<string, ParkingQuotaProfile>;

export type TimeZones = Record<string, TimeZone>;

export type BuildingResourcesEntity = {
  buildingId: string;
} & (
  | {
      status: 'loading';
      resources: Resource[] | undefined;
      floordIds: string[] | undefined;
      errorMessage: string | undefined;
    }
  | {status: 'loaded'; resources: Resource[]; floordIds: string[]; errorMessage: undefined}
  | {status: 'failed'; resources: Resource[] | undefined; floordIds: string[] | undefined; errorMessage: string}
);
